/**
 * Portfolio Upload component with S3 presigned URL support
 * Uses Pica for high-quality Lanczos resampling (same as Photoshop)
 * Supports multiple image uploads with tagging, and drag-to-reorder
 */
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Upload, X, Loader2, Plus, AlertCircle, CheckCircle, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import { API } from "@/App";
import { GENRES, getLabel } from "@/lib/profileOptions";
import Pica from "pica";

// Initialize Pica with high-quality settings
const pica = new Pica({
  features: ["js", "wasm", "ww"], // Use WebAssembly and Web Workers for speed
});

// Maximum dimension for resized images
const MAX_IMAGE_DIMENSION = 2000;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Resize an image using Pica (Lanczos resampling - same as Photoshop)
 */
async function resizeImage(file, maxDimension = MAX_IMAGE_DIMENSION) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = async () => {
      try {
        let { width, height } = img;
        const originalWidth = width;
        const originalHeight = height;
        
        // Calculate new dimensions maintaining aspect ratio
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }
        
        // If no resize needed, return original
        if (width === originalWidth && height === originalHeight) {
          const blob = await new Promise(res => {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            canvas.toBlob(res, file.type === "image/png" ? "image/png" : "image/jpeg", 0.92);
          });
          resolve(blob);
          return;
        }
        
        // Create source canvas
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = originalWidth;
        srcCanvas.height = originalHeight;
        const srcCtx = srcCanvas.getContext("2d");
        srcCtx.drawImage(img, 0, 0);
        
        // Create destination canvas
        const destCanvas = document.createElement("canvas");
        destCanvas.width = width;
        destCanvas.height = height;
        
        // Use Pica for high-quality resize (Lanczos3 filter)
        await pica.resize(srcCanvas, destCanvas, {
          quality: 3, // Highest quality (Lanczos3)
          alpha: file.type === "image/png",
          unsharpAmount: 80,    // Light sharpening to counteract softening
          unsharpRadius: 0.6,
          unsharpThreshold: 2
        });
        
        // Convert to blob with high quality
        const blob = await pica.toBlob(
          destCanvas,
          file.type === "image/png" ? "image/png" : "image/jpeg",
          0.92 // High quality JPEG
        );
        
        resolve(blob);
      } catch (err) {
        reject(err);
      } finally {
        URL.revokeObjectURL(img.src);
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Get image dimensions
 */
async function getImageDimensions(file) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: 0, height: 0 });
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/**
 * One draggable tile in the portfolio grid.
 * The whole tile is the drag handle; the remove button still takes plain clicks
 * because the sensors wait for a small movement (mouse) or a press-and-hold (touch).
 */
function SortableImage({ item, idx, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.url });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      aria-label={`Portfolio image ${idx + 1}. Press space to pick up and use arrow keys to move.`}
      className={`relative aspect-square rounded-sm overflow-hidden bg-white/[0.05] group cursor-grab active:cursor-grabbing touch-manipulation select-none ${
        isDragging ? "z-10 opacity-80 ring-2 ring-white shadow-2xl" : ""
      }`}
      data-testid={`portfolio-image-${idx}`}
    >
      <img
        src={item.url}
        alt={`Portfolio ${idx + 1}`}
        className="w-full h-full object-cover pointer-events-none"
        draggable={false}
      />

      {/* Position + drag hint */}
      <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 text-white/80 text-xs px-1.5 py-0.5 rounded-sm">
        <GripVertical className="w-3 h-3" />
        {idx + 1}
      </div>

      {/* Overlay with remove button */}
      {!isDragging && (
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <button
            type="button"
            onClick={() => onRemove(idx)}
            className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 transition-colors"
            aria-label={`Remove portfolio image ${idx + 1}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tags display */}
      {item.tags && item.tags.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent pointer-events-none">
          <div className="flex flex-wrap gap-1">
            {item.tags.slice(0, 2).map(tag => (
              <Badge
                key={tag}
                className="bg-white/20 text-white text-xs rounded-sm"
              >
                {getLabel(GENRES, tag)}
              </Badge>
            ))}
            {item.tags.length > 2 && (
              <Badge className="bg-white/20 text-white text-xs rounded-sm">
                +{item.tags.length - 2}
              </Badge>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function PortfolioUpload({ 
  portfolio, 
  setPortfolio, 
  token,
  maxImages = 20,
  className = "",
  onUploadComplete
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, errors: [] });
  const [dragOver, setDragOver] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [autoResize, setAutoResize] = useState(true);
  const fileInputRef = useRef(null);

  // Mouse drags start after 8px of movement so clicks on the remove button still work.
  // Touch drags need a short press-and-hold so the page still scrolls normally on phones.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleSortEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = portfolio.findIndex(item => item.url === active.id);
    const to = portfolio.findIndex(item => item.url === over.id);
    if (from === -1 || to === -1) return;
    setPortfolio(arrayMove(portfolio, from, to));
  };

  const uploadFile = async (file, index, total) => {
    // Validate file type
    const supportedTypes = ["image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!file.type.startsWith("image/") || !supportedTypes.includes(file.type)) {
      return { success: false, error: `${file.name}: Unsupported format. Use JPG, PNG, or WebP.` };
    }

    try {
      let uploadFile = file;
      
      // Get dimensions to check if resize needed
      const dimensions = await getImageDimensions(file);
      const isLargeImage = dimensions.width > MAX_IMAGE_DIMENSION || dimensions.height > MAX_IMAGE_DIMENSION;
      
      // Check file size and resize if needed
      if (file.size > MAX_FILE_SIZE || (autoResize && isLargeImage)) {
        if (autoResize) {
          try {
            const resizedBlob = await resizeImage(file, MAX_IMAGE_DIMENSION);
            uploadFile = new File([resizedBlob], file.name, { type: resizedBlob.type });
            
            // If still too large, try more aggressive compression
            if (uploadFile.size > MAX_FILE_SIZE) {
              const moreCompressed = await resizeImage(file, 1500);
              uploadFile = new File([moreCompressed], file.name, { type: moreCompressed.type });
            }
            
            if (uploadFile.size > MAX_FILE_SIZE) {
              return { 
                success: false, 
                error: `${file.name}: Still ${formatFileSize(uploadFile.size)} after resize. Use a smaller image.` 
              };
            }
          } catch (resizeError) {
            return { success: false, error: `${file.name}: Resize failed - ${resizeError.message}` };
          }
        } else {
          return { 
            success: false, 
            error: `${file.name}: ${formatFileSize(file.size)} exceeds 5MB limit. Enable auto-resize.` 
          };
        }
      }

      // Get presigned URL from backend
      const presignedResponse = await fetch(`${API}/users/presigned-url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          filename: uploadFile.name,
          content_type: uploadFile.type
        })
      });

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json().catch(() => ({}));
        return { 
          success: false, 
          error: `${file.name}: Server error - ${errorData.detail || presignedResponse.status}` 
        };
      }

      const { upload_url, file_url } = await presignedResponse.json();

      // Upload to S3
      const uploadResponse = await fetch(upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": uploadFile.type
        },
        body: uploadFile
      });

      if (!uploadResponse.ok) {
        return { 
          success: false, 
          error: `${file.name}: Upload failed (${uploadResponse.status})` 
        };
      }

      return { success: true, url: file_url };
    } catch (error) {
      console.error("Upload error:", error);
      return { success: false, error: `${file.name}: ${error.message}` };
    }
  };

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const remainingSlots = maxImages - portfolio.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    setUploading(true);
    setUploadProgress({ current: 0, total: filesToUpload.length, errors: [] });

    const errors = [];
    const successfulUploads = [];

    for (let i = 0; i < filesToUpload.length; i++) {
      const file = filesToUpload[i];
      setUploadProgress(prev => ({ ...prev, current: i + 1 }));
      
      const result = await uploadFile(file, i, filesToUpload.length);
      
      if (result.success) {
        successfulUploads.push({
          url: result.url,
          tags: [...selectedTags],
          caption: ""
        });
      } else {
        errors.push(result.error);
      }
    }

    if (successfulUploads.length > 0) {
      setPortfolio([...portfolio, ...successfulUploads]);
      toast.success(`${successfulUploads.length} image(s) uploaded successfully!`);
      setSelectedTags([]); // Reset tags after upload
    }

    if (errors.length > 0) {
      setUploadProgress(prev => ({ ...prev, errors }));
      // Show summary toast
      if (errors.length === filesToUpload.length) {
        toast.error("All uploads failed. See details below.");
      } else {
        toast.warning(`${errors.length} of ${filesToUpload.length} uploads failed.`);
      }
    } else {
      setUploadProgress({ current: 0, total: 0, errors: [] });
    }

    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (e) => {
    handleFileSelect(e.target.files);
    // Reset input to allow selecting the same file again
    e.target.value = "";
  };

  const removeImage = (index) => {
    setPortfolio(portfolio.filter((_, i) => i !== index));
    toast.success("Image removed");
  };

  const toggleTag = (tag) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearErrors = () => {
    setUploadProgress(prev => ({ ...prev, errors: [] }));
  };

  return (
    <div className={className}>
      {/* Upload Zone */}
      <div
        className={`relative border-2 border-dashed rounded-sm p-8 text-center transition-colors cursor-pointer ${
          dragOver ? "border-white bg-white/5" : "border-white/20 hover:border-white/40"
        }`}
        onClick={handleClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        data-testid="portfolio-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={handleInputChange}
          className="hidden"
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-10 h-10 text-white/60 animate-spin" />
            <p className="text-white/60">
              Uploading {uploadProgress.current} of {uploadProgress.total}...
            </p>
            <div className="w-48 h-1 bg-white/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white transition-all"
                style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
              />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
              <Upload className="w-8 h-8 text-white/40" />
            </div>
            <div>
              <p className="text-white font-medium">Drop images here or click to upload</p>
              <p className="text-white/40 text-sm mt-1">JPG, PNG or WebP. Max 5MB each.</p>
              <p className="text-white/30 text-xs mt-1">Recommended: 2000px max on longest side</p>
            </div>
          </div>
        )}
      </div>

      {/* Auto-resize toggle */}
      <div className="flex items-center justify-between mt-4 p-3 bg-white/[0.05] rounded-sm">
        <div>
          <span className="text-white/80 text-sm font-medium">Auto-resize large images</span>
          <p className="text-white/40 text-xs">Automatically resize images over 2000px or 5MB</p>
        </div>
        <Switch
          checked={autoResize}
          onCheckedChange={setAutoResize}
          className="data-[state=checked]:bg-white"
        />
      </div>

      {/* Error Messages */}
      {uploadProgress.errors.length > 0 && (
        <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-red-400 font-medium">Upload Errors</span>
            </div>
            <button 
              onClick={clearErrors}
              className="text-white/40 hover:text-white text-sm"
            >
              Dismiss
            </button>
          </div>
          <ul className="space-y-1">
            {uploadProgress.errors.map((error, idx) => (
              <li key={idx} className="text-red-400/80 text-sm">• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tag Selection for next upload */}
      <div className="mt-4">
        <p className="text-white/60 text-sm mb-2">Pre-select tags for your next upload:</p>
        <div className="flex flex-wrap gap-2">
          {GENRES.slice(0, 10).map(genre => {
            const isSelected = selectedTags.includes(genre.value);
            return (
              <button
                key={genre.value}
                type="button"
                onClick={() => toggleTag(genre.value)}
                className={`px-3 py-1 text-sm rounded-sm transition-all ${
                  isSelected 
                    ? "bg-white text-black" 
                    : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
              >
                {genre.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Portfolio Grid */}
      {portfolio.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-white/60 text-sm">
              {portfolio.length}/{maxImages} images
            </p>
            {portfolio.length >= 10 && (
              <span className="flex items-center gap-1 text-coral text-sm">
                <CheckCircle className="w-4 h-4" />
                Minimum reached
              </span>
            )}
          </div>
          
          <p className="text-white/40 text-xs mb-3">
            Drag images to reorder them. The first images are the ones studios see first.
          </p>

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSortEnd}>
          <SortableContext items={portfolio.map(item => item.url)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {portfolio.map((item, idx) => (
              <SortableImage key={item.url} item={item} idx={idx} onRemove={removeImage} />
            ))}

            {/* Add more button if under limit */}
            {portfolio.length < maxImages && (
              <div
                onClick={handleClick}
                className="aspect-square rounded-sm border-2 border-dashed border-white/20 hover:border-white/40 flex items-center justify-center cursor-pointer transition-colors bg-white/[0.05]/50"
              >
                <Plus className="w-8 h-8 text-white/40" />
              </div>
            )}
          </div>
          </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
}

export default PortfolioUpload;
